import { useEffect, useState, useCallback, useId } from "react";
import { PlatformManagerService } from "@/lib/platformManager/platformManager.service";
import type {
	UserWithBalance,
	SubscriptionPlan,
} from "@/lib/platformManager/platformManager.service";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
	TrendingUp,
	Shield,
	AlertTriangle,
	RefreshCw,
	Lock,
	Edit,
	MoreVertical,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/contexts/auth.context";
import i18next from "i18next";

/**
 * Check if user's email is from an authorized domain for subscription management
 */
function isAuthorizedForSubscriptionManagement(
	email: string | null | undefined,
): boolean {
	if (!email) return false;
	const allowedDomains = ["@taxpeer.de", "@podhive.ai"];
	const emailLower = email.toLowerCase();
	return allowedDomains.some((domain) => emailLower.endsWith(domain));
}

/**
 * Extract multilingual string value based on current language
 */
function extractMultilingualString(
	value: Record<string, string> | string | null | undefined,
): string {
	if (!value) return "";
	if (typeof value === "string") return value;
	const currentLang = i18next.language?.split("-")[0] || "en";
	return value[currentLang] || value.en || Object.values(value)[0] || "";
}

/**
 * Convert form data to multilingual format
 */
function createMultilingualString(
	en: string,
	de: string,
): Record<string, string> | null {
	if (!en && !de) return null;
	return {
		en: en || de || "",
		de: de || en || "",
	};
}

/**
 * Convert form features to multilingual format
 */
function createMultilingualFeatures(
	enFeatures: string[],
	deFeatures: string[],
): Record<string, string[]> | null {
	if (enFeatures.length === 0 && deFeatures.length === 0) return null;
	return {
		en: enFeatures.length > 0 ? enFeatures : deFeatures,
		de: deFeatures.length > 0 ? deFeatures : enFeatures,
	};
}

export default function SubscriptionManagementPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuth();
	const [users, setUsers] = useState<UserWithBalance[]>([]);
	const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
	const [loading, setLoading] = useState(true);

	// Check if current user can manage subscriptions
	const canManageSubscriptions = isAuthorizedForSubscriptionManagement(
		user?.email,
	);

	// Upgrade modal state
	const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<UserWithBalance | null>(
		null,
	);
	const [selectedUserDetails, setSelectedUserDetails] = useState<any | null>(
		null,
	);
	const [selectedPlanId, setSelectedPlanId] = useState<string>("");
	const [upgradeLoading, setUpgradeLoading] = useState(false);

	// Reset billing modal state
	const [resetBillingModalOpen, setResetBillingModalOpen] = useState(false);
	const [resetBillingLoading, setResetBillingLoading] = useState(false);

	// Edit plan modal state
	const [editPlanModalOpen, setEditPlanModalOpen] = useState(false);
	const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
	const [editPlanLoading, setEditPlanLoading] = useState(false);
	const [canEditProductId, setCanEditProductId] = useState(false);
	const [availablePrices, setAvailablePrices] = useState<
		Array<{
			id: string;
			unitAmount: number | null;
			currency: string;
			type: "one_time" | "recurring";
			recurring?: {
				interval: string;
				intervalCount: number;
			} | null;
			nickname?: string | null;
			active: boolean;
		}>
	>([]);
	const [loadingPrices, setLoadingPrices] = useState(false);
	const [editLanguage, setEditLanguage] = useState<"en" | "de">("en");
	const [planFormData, setPlanFormData] = useState({
		descriptionEn: "",
		descriptionDe: "",
		ctaEn: "",
		ctaDe: "",
		price: "",
		originalPrice: "",
		stripeProductId: "",
		stripePriceId: "",
		highlight: false,
		showInFrontend: true,
		featuresEn: [] as string[],
		featuresDe: [] as string[],
	});

	const platformManagerService = PlatformManagerService.getInstance();
	const upgradePlanSelectId = useId();

	// Format date helper
	const formatDate = (dateString: string | null) => {
		if (!dateString) return t("common.never");
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	// Load data function
	const loadData = useCallback(async () => {
		try {
			setLoading(true);
			const [usersData, plansData] = await Promise.all([
				platformManagerService.getAllUsers(),
				platformManagerService.getAllSubscriptionPlans(),
			]);
			setUsers(usersData);
			setPlans(plansData);
		} catch (error) {
			console.error("Failed to load subscription management data:", error);
			toast.error(t("platformManager.subscription.errors.loadFailed"));
		} finally {
			setLoading(false);
		}
	}, [platformManagerService, t]);

	// Load data on mount
	useEffect(() => {
		loadData();
	}, [loadData]);

	// Load form data when editing plan changes
	useEffect(() => {
		if (!editingPlan || !editPlanModalOpen) return;

		// Extract multilingual description
		const description =
			typeof editingPlan.description === "object" &&
			editingPlan.description !== null
				? editingPlan.description
				: null;

		// Extract multilingual CTA
		const cta =
			typeof editingPlan.cta === "object" && editingPlan.cta !== null
				? editingPlan.cta
				: null;

		// Extract multilingual features
		const features =
			typeof editingPlan.features === "object" &&
			editingPlan.features !== null &&
			!Array.isArray(editingPlan.features)
				? editingPlan.features
				: null;

		setPlanFormData({
			descriptionEn: description?.en || "",
			descriptionDe: description?.de || "",
			ctaEn: cta?.en || "",
			ctaDe: cta?.de || "",
			price: editingPlan.price?.toString() || "",
			originalPrice: editingPlan.originalPrice?.toString() || "",
			stripeProductId: editingPlan.stripeProductId || "",
			stripePriceId: editingPlan.stripePriceId || "",
			highlight: editingPlan.highlight || false,
			showInFrontend: editingPlan.showInFrontend ?? true,
			featuresEn: features?.en || [],
			featuresDe: features?.de || [],
		});
	}, [editingPlan, editPlanModalOpen]);

	// Fetch prices when editing plan changes
	useEffect(() => {
		const fetchPrices = async () => {
			if (!editingPlan || !editPlanModalOpen) return;

			const productId = editingPlan.stripeProductId?.trim() || "";
			const currentPriceId = editingPlan.stripePriceId || "";

			// Skip if empty or free plan
			if (!productId || productId === "plan_free") {
				setAvailablePrices([]);
				return;
			}

			// Only fetch if product ID looks valid (starts with prod_)
			if (!productId.startsWith("prod_")) {
				setAvailablePrices([]);
				return;
			}

			try {
				setLoadingPrices(true);
				const prices =
					await platformManagerService.getStripePricesForProduct(productId);
				setAvailablePrices(prices);

				// Ensure current price ID is preserved in form data
				if (currentPriceId) {
					const priceExists = prices.some((p) => p.id === currentPriceId);
					if (priceExists) {
						setPlanFormData((prev) => ({
							...prev,
							stripePriceId: currentPriceId,
						}));
					}
				}
			} catch (error) {
				console.error("Failed to fetch prices:", error);
				toast.error("Failed to load prices for this product");
				setAvailablePrices([]);
			} finally {
				setLoadingPrices(false);
			}
		};

		if (editPlanModalOpen && editingPlan) {
			fetchPrices();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editPlanModalOpen, editingPlan?.id]);

	// Open edit plan modal
	const openEditPlanModal = (plan: SubscriptionPlan) => {
		if (!canManageSubscriptions) {
			toast.error(t("platformManager.subscription.unauthorized"));
			return;
		}
		setEditLanguage("en"); // Reset to English
		setCanEditProductId(false); // Reset product ID editing
		setAvailablePrices([]); // Reset prices - will be fetched by useEffect
		setEditingPlan(plan); // This will trigger useEffect to load form data
		setEditPlanModalOpen(true);
	};

	// Format price for display
	const formatPrice = (price: {
		unitAmount: number | null;
		currency: string;
		type: "one_time" | "recurring";
		recurring?: {
			interval: string;
			intervalCount: number;
		} | null;
		nickname?: string | null;
	}) => {
		const amount = price.unitAmount
			? (price.unitAmount / 100).toFixed(2)
			: "0.00";
		const currency = price.currency.toUpperCase();
		const interval = price.recurring
			? `/${price.recurring.intervalCount === 1 ? price.recurring.interval : `${price.recurring.intervalCount} ${price.recurring.interval}s`}`
			: "";
		const nickname = price.nickname ? ` (${price.nickname})` : "";
		return `${currency} ${amount}${interval}${nickname}`;
	};

	// Handle save plan changes
	const handleSavePlan = async () => {
		if (!editingPlan) return;

		try {
			setEditPlanLoading(true);
			await platformManagerService.updateSubscriptionPlan(editingPlan.id, {
				description: createMultilingualString(
					planFormData.descriptionEn,
					planFormData.descriptionDe,
				),
				cta: createMultilingualString(planFormData.ctaEn, planFormData.ctaDe),
				price: planFormData.price ? parseFloat(planFormData.price) : null,
				originalPrice: planFormData.originalPrice
					? parseFloat(planFormData.originalPrice)
					: null,
				stripeProductId: planFormData.stripeProductId || null,
				stripePriceId: planFormData.stripePriceId || null,
				highlight: planFormData.highlight,
				showInFrontend: planFormData.showInFrontend,
				features: createMultilingualFeatures(
					planFormData.featuresEn,
					planFormData.featuresDe,
				),
			});

			toast.success("Plan updated successfully");
			setEditPlanModalOpen(false);
			setEditingPlan(null);
			loadData(); // Reload plans
		} catch (error) {
			console.error("Failed to update plan:", error);
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to update subscription plan",
			);
		} finally {
			setEditPlanLoading(false);
		}
	};

	// Handle adding/removing features
	const addFeature = (lang: "en" | "de") => {
		if (lang === "en") {
			setPlanFormData({
				...planFormData,
				featuresEn: [...planFormData.featuresEn, ""],
			});
		} else {
			setPlanFormData({
				...planFormData,
				featuresDe: [...planFormData.featuresDe, ""],
			});
		}
	};

	const updateFeature = (index: number, value: string, lang: "en" | "de") => {
		if (lang === "en") {
			const newFeatures = [...planFormData.featuresEn];
			newFeatures[index] = value;
			setPlanFormData({ ...planFormData, featuresEn: newFeatures });
		} else {
			const newFeatures = [...planFormData.featuresDe];
			newFeatures[index] = value;
			setPlanFormData({ ...planFormData, featuresDe: newFeatures });
		}
	};

	const removeFeature = (index: number, lang: "en" | "de") => {
		if (lang === "en") {
			const newFeatures = planFormData.featuresEn.filter((_, i) => i !== index);
			setPlanFormData({ ...planFormData, featuresEn: newFeatures });
		} else {
			const newFeatures = planFormData.featuresDe.filter((_, i) => i !== index);
			setPlanFormData({ ...planFormData, featuresDe: newFeatures });
		}
	};

	// Open upgrade modal
	const openUpgradeModal = async (user: UserWithBalance) => {
		if (!canManageSubscriptions) {
			toast.error(t("platformManager.subscription.unauthorized"));
			return;
		}

		setSelectedUser(user);
		setUpgradeModalOpen(true);
		setSelectedPlanId("");

		// Fetch user details to get current plan
		try {
			const details = await platformManagerService.getUserDetails(user.id);
			setSelectedUserDetails(details);
		} catch (error) {
			console.error("Failed to load user details:", error);
			toast.error(t("platformManager.errors.loadUserDetailsFailed"));
		}
	};

	// Handle subscription upgrade
	const handleUpgrade = async () => {
		if (!selectedUser || !selectedPlanId) {
			toast.error(t("platformManager.subscription.selectPlan"));
			return;
		}

		const selectedPlan = plans.find((p) => p.id === selectedPlanId);
		if (!selectedPlan) {
			toast.error(t("platformManager.subscription.invalidPlan"));
			return;
		}

		try {
			setUpgradeLoading(true);
			await platformManagerService.upgradeUserSubscription(
				selectedUser.id,
				selectedPlanId,
			);
			toast.success(
				t("platformManager.subscription.upgradeSuccess", {
					email: selectedUser.email,
					plan: selectedPlan.name,
				}),
			);
			setUpgradeModalOpen(false);
			loadData(); // Reload data to reflect changes
		} catch (error) {
			console.error("Failed to upgrade subscription:", error);
			toast.error(t("platformManager.subscription.upgradeFailed"));
		} finally {
			setUpgradeLoading(false);
		}
	};

	// Open reset billing modal
	const openResetBillingModal = (user: UserWithBalance) => {
		if (!canManageSubscriptions) {
			toast.error(t("platformManager.subscription.unauthorized"));
			return;
		}

		setSelectedUser(user);
		setResetBillingModalOpen(true);
	};

	// Handle reset billing cycle
	const handleResetBilling = async () => {
		if (!selectedUser) return;

		try {
			setResetBillingLoading(true);
			await platformManagerService.resetUserBillingCycle(selectedUser.id);
			toast.success(
				t("platformManager.subscription.resetBillingSuccess", {
					email: selectedUser.email,
				}),
			);
			setResetBillingModalOpen(false);
			loadData(); // Reload data to reflect changes
		} catch (error) {
			console.error("Failed to reset billing cycle:", error);
			toast.error(t("platformManager.subscription.resetBillingFailed"));
		} finally {
			setResetBillingLoading(false);
		}
	};

	// Define table columns
	const columns: DataTableColumn<UserWithBalance>[] = [
		{
			key: "email",
			label: t("platformManager.userManagement.columns.email"),
			sortable: true,
			accessor: (user) => user.email,
			render: (user) => <span className="font-medium">{user.email}</span>,
		},
		{
			key: "name",
			label: t("platformManager.userManagement.columns.name"),
			sortable: true,
			accessor: (user) => user.name || "",
			render: (user) => user.name || "-",
		},
		{
			key: "coinBalance",
			label: t("platformManager.userManagement.columns.balance"),
			sortable: true,
			accessor: (user) => user.coinBalance,
			render: (user) => (
				<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
					{user.coinBalance.toLocaleString()}
				</span>
			),
		},
		{
			key: "currentPlanName",
			label: "Current Plan",
			sortable: true,
			accessor: (user) => user.currentPlanName || "",
			render: (user) => (
				<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
					{user.currentPlanName}
				</span>
			),
		},
		{
			key: "lastBillingReset",
			label: "Last Billing Reset",
			sortable: true,
			accessor: (user) => user.lastBillingReset || "",
			render: (user) => {
				if (!user.lastBillingReset) {
					return <span className="text-muted-foreground">Never</span>;
				}
				const resetDate = new Date(user.lastBillingReset);
				const now = new Date();

				// Calculate next reset date (30 days after last reset)
				const nextReset = new Date(resetDate);
				nextReset.setDate(nextReset.getDate() + 30);
				const daysUntilNext = Math.ceil(
					(nextReset.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
				);

				return (
					<div className="text-sm">
						<div>{formatDate(user.lastBillingReset)}</div>
						<div className="text-xs text-muted-foreground">
							{daysUntilNext > 0
								? `Next reset in ${daysUntilNext} day${daysUntilNext !== 1 ? "s" : ""}`
								: "Reset due"}
						</div>
					</div>
				);
			},
		},
		{
			key: "subscriptionEndsAt",
			label: "Subscription Ends",
			sortable: true,
			accessor: (user) => user.subscriptionEndsAt || "",
			render: (user) => {
				if (!user.subscriptionEndsAt) {
					return <span className="text-muted-foreground">-</span>;
				}
				const endDate = new Date(user.subscriptionEndsAt);
				const now = new Date();
				const isExpired = endDate < now;

				return (
					<span className={isExpired ? "text-red-600 font-medium" : ""}>
						{formatDate(user.subscriptionEndsAt)}
						{isExpired && <span className="ml-1 text-xs">(Expired)</span>}
					</span>
				);
			},
		},
		{
			key: "lastActive",
			label: t("platformManager.userManagement.columns.lastActive"),
			sortable: true,
			accessor: (user) => user.lastActive || "",
			render: (user) => formatDate(user.lastActive),
		},
		{
			key: "actions",
			label: t("platformManager.subscription.columns.actions"),
			sortable: false,
			render: (user) => (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							size="sm"
							variant="ghost"
							disabled={!canManageSubscriptions}
							className="h-8 w-8 p-0"
						>
							<MoreVertical className="h-4 w-4" />
							<span className="sr-only">Open menu</span>
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							onClick={() => openUpgradeModal(user)}
							disabled={!canManageSubscriptions}
						>
							<TrendingUp className="h-4 w-4 mr-2" />
							{t("platformManager.subscription.upgrade")}
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() => openResetBillingModal(user)}
							disabled={!canManageSubscriptions}
						>
							<RefreshCw className="h-4 w-4 mr-2" />
							Reset Billing Cycle
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			),
		},
	];

	if (loading) {
		return (
			<div className="flex items-center justify-center h-full">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto" />
					<p className="mt-4 text-gray-600">{t("common.loading")}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto p-6 space-y-6">
			{/* Header */}
			<div className="flex justify-between items-start">
				<div>
					<h1 className="text-3xl font-bold">
						{t("platformManager.subscription.title")}
					</h1>
					<p className="text-gray-600">
						{t("platformManager.subscription.description")}
					</p>
				</div>
				<Button onClick={() => navigate("/platform-manager")} variant="outline">
					{t("platformManager.subscription.backToManager")}
				</Button>
			</div>

			{/* Access Notice */}
			{!canManageSubscriptions && (
				<Card className="border-orange-200 bg-orange-50">
					<CardContent className="pt-6">
						<div className="flex items-start gap-3">
							<Lock className="h-5 w-5 text-orange-600 mt-0.5" />
							<div>
								<h3 className="font-semibold text-orange-900">
									{t("platformManager.subscription.restrictedAccess")}
								</h3>
								<p className="text-sm text-orange-800 mt-1">
									{t("platformManager.subscription.restrictedAccessDesc")}
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Subscription Plans Overview */}
			<div>
				<div className="flex justify-between items-center mb-4">
					<h2 className="text-xl font-semibold">
						{t("platformManager.subscription.plans")}
					</h2>
				</div>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
					{plans.map((plan) => (
						<Card key={plan.id}>
							<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle className="text-sm font-medium">
									{plan.name}
								</CardTitle>
								<Shield className="h-4 w-4 text-muted-foreground" />
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									{plan.description && (
										<div className="text-xs text-muted-foreground italic">
											{extractMultilingualString(plan.description)}
										</div>
									)}
									<div className="text-xs text-muted-foreground">
										{t("platformManager.subscription.monthlyCoins")}:{" "}
										{plan.limits.monthlyCoins}
									</div>
									<div className="text-xs text-muted-foreground">
										{t("platformManager.subscription.maxChats")}:{" "}
										{plan.limits.maxChats === -1
											? t("platformManager.userDetails.unlimited")
											: plan.limits.maxChats}
									</div>
									<div className="text-xs text-muted-foreground">
										{t("platformManager.subscription.maxStorageEpisodes")}:{" "}
										{plan.limits.maxStorageEpisodes === -1
											? t("platformManager.userDetails.unlimited")
											: plan.limits.maxStorageEpisodes}
									</div>
									{plan.price !== null && plan.price !== undefined && (
										<div className="text-xs font-medium">
											€{plan.price.toFixed(2)}
											{plan.originalPrice &&
												plan.originalPrice > plan.price && (
													<span className="text-muted-foreground line-through ml-2">
														€{plan.originalPrice.toFixed(2)}
													</span>
												)}
										</div>
									)}
									<div className="flex items-center gap-2 mt-3">
										<Button
											size="sm"
											variant="outline"
											onClick={() => openEditPlanModal(plan)}
											className="flex-1"
										>
											<Edit className="h-3 w-3 mr-1" />
											Edit
										</Button>
									</div>
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			</div>

			{/* Users Table */}
			<DataTable
				data={users}
				columns={columns}
				title={t("platformManager.subscription.userSubscriptions")}
				description={t("platformManager.subscription.userSubscriptionsDesc")}
				searchable
				searchPlaceholder={t(
					"platformManager.userManagement.searchPlaceholder",
				)}
				defaultSortField="lastActive"
				defaultSortOrder="desc"
				loading={loading}
				emptyMessage={t("platformManager.userManagement.noUsersFound")}
			/>

			{/* Upgrade Subscription Modal */}
			<Dialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{t("platformManager.subscription.upgradeUser")}
						</DialogTitle>
						<DialogDescription>
							{t("platformManager.subscription.upgradeUserDescription", {
								email: selectedUser?.email,
							})}
						</DialogDescription>
					</DialogHeader>

					{selectedUserDetails && (
						<div className="space-y-4">
							<div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
								<div className="flex items-start gap-2">
									<AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
									<div className="flex-1">
										<p className="text-sm font-medium text-orange-900">
											{t("platformManager.subscription.upgradeWarning")}
										</p>
										<p className="text-xs text-orange-800 mt-1">
											{t("platformManager.subscription.upgradeWarningDesc")}
										</p>
									</div>
								</div>
							</div>

							<div className="rounded-lg border p-4 space-y-2">
								<div className="text-sm">
									<span className="font-medium">
										{t("platformManager.userDetails.plan")}:
									</span>{" "}
									{selectedUserDetails.subscription.planName}
								</div>
								<div className="text-sm">
									<span className="font-medium">
										{t("platformManager.userDetails.status")}:
									</span>{" "}
									{selectedUserDetails.subscription.status}
								</div>
								{selectedUserDetails.subscription.endsAt && (
									<div className="text-sm">
										<span className="font-medium">
											{t("platformManager.userDetails.endsAt")}:
										</span>{" "}
										{formatDate(selectedUserDetails.subscription.endsAt)}
									</div>
								)}
							</div>

							<div>
								<Label htmlFor={upgradePlanSelectId}>
									{t("platformManager.subscription.selectNewPlan")}
								</Label>
								<select
									id={upgradePlanSelectId}
									className="w-full mt-1 p-2 border rounded-md"
									value={selectedPlanId}
									onChange={(e) => setSelectedPlanId(e.target.value)}
								>
									<option value="">
										{t("platformManager.subscription.selectPlanOption")}
									</option>
									{plans
										.filter(
											(plan) =>
												plan.id !== selectedUserDetails?.user?.currentPlanId,
										)
										.map((plan) => (
											<option key={plan.id} value={plan.id}>
												{plan.name} - {plan.limits.monthlyCoins}{" "}
												{t("coins.coins")}
											</option>
										))}
								</select>
							</div>

							{selectedPlanId && (
								<div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
									<p className="text-sm text-blue-900">
										{t("platformManager.subscription.upgradeInfo")}
									</p>
								</div>
							)}
						</div>
					)}

					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setUpgradeModalOpen(false)}
							disabled={upgradeLoading}
						>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={handleUpgrade}
							disabled={upgradeLoading || !selectedPlanId}
						>
							{upgradeLoading
								? t("common.processing")
								: t("platformManager.subscription.confirmUpgrade")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Edit Plan Modal */}
			<Dialog open={editPlanModalOpen} onOpenChange={setEditPlanModalOpen}>
				<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Edit Plan: {editingPlan?.name}</DialogTitle>
						<DialogDescription>
							Update plan display fields. Changes will be reflected on the
							pricing page.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4">
						{/* Language Toggle */}
						<div className="flex items-center justify-between p-3 bg-muted rounded-lg">
							<Label className="text-sm font-medium">Edit Language</Label>
							<div className="flex items-center gap-3">
								<span
									className={`text-sm ${editLanguage === "en" ? "font-semibold" : "text-muted-foreground"}`}
								>
									English
								</span>
								<Switch
									checked={editLanguage === "de"}
									onCheckedChange={(checked) =>
										setEditLanguage(checked ? "de" : "en")
									}
								/>
								<span
									className={`text-sm ${editLanguage === "de" ? "font-semibold" : "text-muted-foreground"}`}
								>
									Deutsch
								</span>
							</div>
						</div>

						{/* Description - Multilingual */}
						<div className="space-y-2">
							<Label>Description / Tagline</Label>
							{editLanguage === "en" ? (
								<Textarea
									id="descriptionEn"
									value={planFormData.descriptionEn}
									onChange={(e) =>
										setPlanFormData({
											...planFormData,
											descriptionEn: e.target.value,
										})
									}
									placeholder="e.g., Try it first. Hit limits. Upgrade when ready."
									rows={2}
								/>
							) : (
								<Textarea
									id="descriptionDe"
									value={planFormData.descriptionDe}
									onChange={(e) =>
										setPlanFormData({
											...planFormData,
											descriptionDe: e.target.value,
										})
									}
									placeholder="z.B., Probiere es zuerst aus. Erreiche Limits. Upgrade, wenn du bereit bist."
									rows={2}
								/>
							)}
						</div>

						{/* CTA - Multilingual */}
						<div className="space-y-2">
							<Label>Call-to-Action Button Text</Label>
							{editLanguage === "en" ? (
								<Input
									id="ctaEn"
									value={planFormData.ctaEn}
									onChange={(e) =>
										setPlanFormData({ ...planFormData, ctaEn: e.target.value })
									}
									placeholder="e.g., Get Started Free"
								/>
							) : (
								<Input
									id="ctaDe"
									value={planFormData.ctaDe}
									onChange={(e) =>
										setPlanFormData({ ...planFormData, ctaDe: e.target.value })
									}
									placeholder="z.B., Kostenlos loslegen"
								/>
							)}
						</div>

						{/* Price */}
						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label htmlFor="price">Price (€)</Label>
								<Input
									id="price"
									type="number"
									step="0.01"
									value={planFormData.price}
									onChange={(e) =>
										setPlanFormData({ ...planFormData, price: e.target.value })
									}
									placeholder="0.00"
								/>
							</div>
							<div>
								<Label htmlFor="originalPrice">Original Price (€)</Label>
								<Input
									id="originalPrice"
									type="number"
									step="0.01"
									value={planFormData.originalPrice}
									onChange={(e) =>
										setPlanFormData({
											...planFormData,
											originalPrice: e.target.value,
										})
									}
									placeholder="0.00"
								/>
							</div>
						</div>

						{/* Stripe Product ID */}
						<div>
							<div className="flex items-center justify-between mb-2">
								<Label htmlFor="stripeProductId">Stripe Product ID</Label>
								<div className="flex items-center gap-2">
									<Switch
										id="editProductId"
										checked={canEditProductId}
										onCheckedChange={setCanEditProductId}
									/>
									<Label
										htmlFor="editProductId"
										className="text-xs text-muted-foreground cursor-pointer"
									>
										Enable editing
									</Label>
								</div>
							</div>
							<Input
								id="stripeProductId"
								value={planFormData.stripeProductId}
								onChange={(e) =>
									setPlanFormData({
										...planFormData,
										stripeProductId: e.target.value,
									})
								}
								disabled={!canEditProductId}
								placeholder="prod_... or plan_free"
								className={
									!canEditProductId ? "bg-muted cursor-not-allowed" : ""
								}
							/>
							<p className="text-xs text-muted-foreground mt-1">
								{canEditProductId
									? "⚠️ Warning: Changing the Product ID can break existing subscriptions. Only change if absolutely necessary."
									: "The Stripe Product ID for this plan. Use 'plan_free' for the free tier. Enable editing to modify."}
							</p>
						</div>

						{/* Stripe Price ID */}
						<div>
							<Label htmlFor="stripePriceId">Stripe Price ID</Label>
							{planFormData.stripeProductId === "plan_free" ? (
								<Input
									id="stripePriceId"
									value=""
									disabled
									placeholder="No price ID for free plan"
								/>
							) : availablePrices.length > 0 ? (
								<Select
									value={planFormData.stripePriceId || undefined}
									onValueChange={(value) =>
										setPlanFormData({
											...planFormData,
											stripePriceId: value,
										})
									}
									disabled={!canEditProductId}
								>
									<SelectTrigger
										id="stripePriceId"
										className={`w-full ${!canEditProductId ? "bg-muted cursor-not-allowed opacity-60" : ""}`}
									>
										<SelectValue placeholder="Select a price..." />
									</SelectTrigger>
									<SelectContent>
										{availablePrices.map((price) => (
											<SelectItem key={price.id} value={price.id}>
												{price.id} - {formatPrice(price)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							) : loadingPrices ? (
								<Input
									id="stripePriceId"
									value=""
									disabled
									placeholder="Loading prices..."
								/>
							) : planFormData.stripeProductId &&
								planFormData.stripeProductId.startsWith("prod_") ? (
								<div className="space-y-2">
									<Input
										id="stripePriceId"
										value={planFormData.stripePriceId}
										onChange={(e) =>
											setPlanFormData({
												...planFormData,
												stripePriceId: e.target.value,
											})
										}
										disabled={!canEditProductId}
										placeholder="price_... (no prices found, enter manually)"
										className={
											!canEditProductId ? "bg-muted cursor-not-allowed" : ""
										}
									/>
									<p className="text-xs text-muted-foreground">
										No prices found for this product. Enter price ID manually or
										check the product ID.
									</p>
								</div>
							) : (
								<Input
									id="stripePriceId"
									value={planFormData.stripePriceId}
									onChange={(e) =>
										setPlanFormData({
											...planFormData,
											stripePriceId: e.target.value,
										})
									}
									disabled={!canEditProductId}
									placeholder="price_... (enter product ID first)"
									className={
										!canEditProductId ? "bg-muted cursor-not-allowed" : ""
									}
								/>
							)}
							<p className="text-xs text-muted-foreground mt-1">
								{planFormData.stripeProductId === "plan_free"
									? "Free plans don't have a Stripe price ID"
									: "Select a price from the list, or enter manually if not found"}
							</p>
						</div>

						{/* Features - Multilingual */}
						<div className="space-y-2">
							<div className="flex justify-between items-center">
								<Label>Features</Label>
								<Button
									type="button"
									size="sm"
									variant="outline"
									onClick={() => addFeature(editLanguage)}
								>
									{editLanguage === "en" ? "Add Feature" : "Feature hinzufügen"}
								</Button>
							</div>
							<div className="space-y-2">
								{(editLanguage === "en"
									? planFormData.featuresEn
									: planFormData.featuresDe
								).map((feature, index) => (
									<div key={`${editLanguage}-${index}`} className="flex gap-2">
										<Input
											value={feature}
											onChange={(e) =>
												updateFeature(index, e.target.value, editLanguage)
											}
											placeholder={
												editLanguage === "en"
													? "e.g., 120 coins total"
													: "z.B., 120 Coins insgesamt"
											}
										/>
										<Button
											type="button"
											size="sm"
											variant="ghost"
											onClick={() => removeFeature(index, editLanguage)}
										>
											×
										</Button>
									</div>
								))}
								{(editLanguage === "en"
									? planFormData.featuresEn
									: planFormData.featuresDe
								).length === 0 && (
									<p className="text-sm text-muted-foreground">
										{editLanguage === "en"
											? 'No features added. Click "Add Feature" to add one.'
											: 'Keine Features hinzugefügt. Klicke auf "Feature hinzufügen", um eines hinzuzufügen.'}
									</p>
								)}
							</div>
						</div>

						{/* Toggles */}
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<div>
									<Label htmlFor="highlight">Highlight as "Most Popular"</Label>
									<p className="text-xs text-muted-foreground">
										Shows a badge and special styling on the pricing page
									</p>
								</div>
								<Switch
									id="highlight"
									checked={planFormData.highlight}
									onCheckedChange={(checked) =>
										setPlanFormData({ ...planFormData, highlight: checked })
									}
								/>
							</div>
							<div className="flex items-center justify-between">
								<div>
									<Label htmlFor="showInFrontend">Show in Frontend</Label>
									<p className="text-xs text-muted-foreground">
										Whether this plan appears on the pricing page
									</p>
								</div>
								<Switch
									id="showInFrontend"
									checked={planFormData.showInFrontend}
									onCheckedChange={(checked) =>
										setPlanFormData({
											...planFormData,
											showInFrontend: checked,
										})
									}
								/>
							</div>
						</div>
					</div>

					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setEditPlanModalOpen(false)}
							disabled={editPlanLoading}
						>
							Cancel
						</Button>
						<Button onClick={handleSavePlan} disabled={editPlanLoading}>
							{editPlanLoading ? "Saving..." : "Save Changes"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Reset Billing Cycle Modal */}
			<Dialog
				open={resetBillingModalOpen}
				onOpenChange={setResetBillingModalOpen}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{t("platformManager.subscription.resetBilling")}
						</DialogTitle>
						<DialogDescription>
							{t("platformManager.subscription.resetBillingDescription", {
								email: selectedUser?.email,
							})}
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4">
						<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
							<div className="flex items-start gap-2">
								<AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
								<div className="flex-1">
									<p className="text-sm font-medium text-yellow-900">
										{t("platformManager.subscription.resetBillingWarning")}
									</p>
									<p className="text-xs text-yellow-800 mt-1">
										{t("platformManager.subscription.resetBillingWarningDesc")}
									</p>
								</div>
							</div>
						</div>

						<p className="text-sm text-gray-600">
							{t("platformManager.subscription.resetBillingInfo")}
						</p>
					</div>

					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setResetBillingModalOpen(false)}
							disabled={resetBillingLoading}
						>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleResetBilling} disabled={resetBillingLoading}>
							{resetBillingLoading
								? t("common.processing")
								: t("platformManager.subscription.confirmResetBilling")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
